import * as React from "react";
import { UploadCloud, FileText, Loader2, CheckCircle2, AlertCircle, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { formatNumber } from "../../lib/format";

/**
 * Dropzone — a real drag-and-drop upload target with a hover highlight,
 * friendly file-type / size validation, and a click-to-browse fallback.
 * It validates against `accept` + `maxSizeBytes` and reports rejects with
 * specific, human messages rather than a bare red border. Per-file progress
 * and status are rendered by the consumer via {@link UploadFileRow}.
 */
export interface DropzoneProps {
  onFiles: (files: File[]) => void;
  /** Comma-separated extensions / mime types, e.g. ".pdf,.docx,image/*". */
  accept?: string;
  maxSizeBytes?: number;
  multiple?: boolean;
  disabled?: boolean;
  hint?: string;
  className?: string;
  "data-testid"?: string;
  /** Forwarded to the underlying file input (for testing / labelling). */
  inputTestId?: string;
}

export function humanSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${formatNumber(Math.round(bytes / (1024 * 1024)))} MB`;
  return `${formatNumber(Math.max(1, Math.round(bytes / 1024)))} KB`;
}

function matchesAccept(file: File, accept?: string): boolean {
  if (!accept) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return accept.split(",").some((raw) => {
    const token = raw.trim().toLowerCase();
    if (!token) return false;
    if (token.startsWith(".")) return name.endsWith(token);
    if (token.endsWith("/*")) return type.startsWith(token.slice(0, -1));
    return type === token;
  });
}

export function Dropzone({
  onFiles,
  accept,
  maxSizeBytes,
  multiple = false,
  disabled = false,
  hint,
  className,
  "data-testid": testId,
  inputTestId,
}: DropzoneProps) {
  const [dragging, setDragging] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleFiles = React.useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      const files = Array.from(fileList);
      const accepted: File[] = [];
      for (const file of files) {
        if (!matchesAccept(file, accept)) {
          setError(`"${file.name}" isn't a supported file type. Accepted: ${accept}.`);
          continue;
        }
        if (maxSizeBytes && file.size > maxSizeBytes) {
          setError(`"${file.name}" is too large (max ${humanSize(maxSizeBytes)}).`);
          continue;
        }
        accepted.push(file);
      }
      if (accepted.length > 0) {
        setError(null);
        onFiles(multiple ? accepted : accepted.slice(0, 1));
      }
    },
    [accept, maxSizeBytes, multiple, onFiles],
  );

  return (
    <div className={className} data-testid={testId}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!disabled) handleFiles(e.dataTransfer.files);
        }}
        className={cn(
          "flex w-full flex-col items-center justify-center gap-space-2 rounded-lg border-2 border-dashed px-space-5 py-space-6 text-center transition-colors duration-fast ease-standard",
          "focus-visible:outline-none focus-visible:shadow-goldRing",
          disabled && "opacity-40 pointer-events-none",
          dragging
            ? "border-gold bg-gold/10"
            : "border-border-light bg-white-warm hover:border-gold",
        )}
      >
        <UploadCloud size={28} className="text-gold-shadow" aria-hidden />
        <span className="text-small font-semibold text-onyx">
          Drag &amp; drop {multiple ? "files" : "a file"} here, or click to browse
        </span>
        {hint && <span className="text-caption text-gray-mid">{hint}</span>}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="sr-only"
        data-testid={inputTestId}
        onChange={(e) => handleFiles(e.target.files)}
      />
      {error && (
        <p role="alert" className="mt-space-2 flex items-center gap-space-1 text-caption text-status-error">
          <AlertCircle size={14} aria-hidden /> {error}
        </p>
      )}
    </div>
  );
}

export type UploadFileState = "ready" | "uploading" | "scanning" | "done" | "error";

export interface UploadFileRowProps {
  name: string;
  sizeBytes?: number;
  state: UploadFileState;
  /** 0–100; shown as a bar while uploading. */
  progress?: number;
  message?: string;
  onRemove?: () => void;
  className?: string;
}

const STATE_META: Record<UploadFileState, { icon: React.ReactNode; label: string }> = {
  ready: { icon: <FileText size={16} className="text-gray-mid" aria-hidden />, label: "Ready" },
  uploading: { icon: <Loader2 size={16} className="text-gold motion-safe:animate-spin" aria-hidden />, label: "Uploading" },
  scanning: { icon: <Loader2 size={16} className="text-status-info motion-safe:animate-spin" aria-hidden />, label: "Scanning" },
  done: { icon: <CheckCircle2 size={16} className="text-status-success" aria-hidden />, label: "Ready" },
  error: { icon: <AlertCircle size={16} className="text-status-error" aria-hidden />, label: "Error" },
};

export function UploadFileRow({
  name,
  sizeBytes,
  state,
  progress,
  message,
  onRemove,
  className,
}: UploadFileRowProps) {
  const meta = STATE_META[state];
  return (
    <div
      className={cn(
        "flex items-center gap-space-3 rounded-md border border-border-light bg-white-pure px-space-3 py-space-2",
        className,
      )}
    >
      <span className="shrink-0">{meta.icon}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-space-2">
          <span className="truncate text-small text-onyx" title={name}>
            {name}
          </span>
          <span className="shrink-0 text-caption text-gray-mid">
            {message ?? meta.label}
            {sizeBytes != null && ` · ${humanSize(sizeBytes)}`}
          </span>
        </div>
        {(state === "uploading" || state === "scanning") && (
          <div className="mt-space-1 h-1 w-full overflow-hidden rounded-pill bg-white-warm">
            <div
              className="h-full rounded-pill bg-gold transition-[width] duration-normal ease-standard"
              style={{ width: `${Math.min(100, Math.max(0, progress ?? 0))}%` }}
            />
          </div>
        )}
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${name}`}
          className="shrink-0 rounded-full p-space-1 text-gray-mid hover:bg-white-warm hover:text-onyx focus-visible:outline-none focus-visible:shadow-goldRing"
        >
          <X size={14} aria-hidden />
        </button>
      )}
    </div>
  );
}
